import { c as _c } from "react/compiler-runtime";
import { clearSsoReauthPending, getPendingSsoReauth, githubReconnectLogin, subscribeSsoReauthPending } from "@/lib/github-reauth";
import type { SsoBlockedOrg } from "@/hooks/useConnectedGithubOrgs";
import { useEffect, useSyncExternalStore } from "react";
import { toast } from "sonner";
const TOAST_ID = "sso-reconnect";

/**
 * Prompt the user to finish reconnecting GitHub after authorizing SAML SSO.
 *
 * Exported separately so Storybook can render the exact production toast.
 */
export function showSsoReconnectToast(onReconnect: () => void = githubReconnectLogin): void {
  // Sonner only routes the close button and swipes through onDismiss, not
  // through cancel.onClick, so both paths must clear the pending flag.
  const dismiss = () => clearSsoReauthPending();
  toast("Authorized on GitHub?", {
    id: TOAST_ID,
    description: "Reconnect to refresh your access.",
    duration: Infinity,
    action: {
      label: "Reconnect",
      onClick: onReconnect
    },
    cancel: {
      label: "Dismiss",
      onClick: dismiss
    },
    onDismiss: dismiss
  });
}
const NO_PENDING_ORGS: string[] = [];
// useSyncExternalStore needs a stable snapshot for unchanged state, so key
// the parsed org list on the raw stored value.
let lastPendingKey = "";
let lastPendingOrgs = NO_PENDING_ORGS;
const pendingOrgsSnapshot = (): string[] => {
  const pending = getPendingSsoReauth();
  const key = pending ? pending.orgs.join("\n") : "";
  if (key !== lastPendingKey) {
    lastPendingKey = key;
    lastPendingOrgs = pending ? pending.orgs : NO_PENDING_ORGS;
  }
  return lastPendingOrgs;
};
const serverSnapshot = () => NO_PENDING_ORGS;

/**
 * Watches for the user returning to the dashboard tab after clicking an org's
 * SSO "Authorize" link, and prompts them to complete the second half of the
 * recovery: a silent OAuth re-login that re-authorizes Blacksmith's token for
 * the org(s) they just established SAML sessions for.
 *
 * Returns the blocked orgs whose reconnect is still pending, so the org
 * switcher can swap their Authorize action for Reconnect.
 *
 * Pass `undefined` while org data is still loading: we can't yet tell whether
 * any orgs are blocked, so the pending flag must be left alone (a full page
 * reload after clicking Authorize would otherwise wipe it before data loads).
 */
export function useSsoReauthPrompt(ssoBlockedOrgs) {
  const $ = _c(4);
  const isLoaded = ssoBlockedOrgs !== undefined;
  const hasBlockedOrgs = (ssoBlockedOrgs?.length ?? 0) > 0;
  const pendingOrgs = useSyncExternalStore(subscribeSsoReauthPending, pendingOrgsSnapshot, serverSnapshot);
  let t0;
  let t1;
  if ($[0] !== hasBlockedOrgs || $[1] !== isLoaded) {
    t0 = () => {
      if (!isLoaded) {
        return;
      }
      if (!hasBlockedOrgs) {
        clearSsoReauthPending();
        toast.dismiss(TOAST_ID);
        return;
      }
      const maybePrompt = _temp;
      maybePrompt();
      window.addEventListener("focus", maybePrompt);
      document.addEventListener("visibilitychange", maybePrompt);
      return () => {
        window.removeEventListener("focus", maybePrompt);
        document.removeEventListener("visibilitychange", maybePrompt);
      };
    };
    t1 = [isLoaded, hasBlockedOrgs];
    $[0] = hasBlockedOrgs;
    $[1] = isLoaded;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return isLoaded && hasBlockedOrgs ? pendingOrgs : NO_PENDING_ORGS;
}
function _temp() {
  if (document.visibilityState !== "visible") {
    return;
  }
  if (!getPendingSsoReauth()) {
    return;
  }
  showSsoReconnectToast();
}